import { useMemo, useState, useEffect } from 'react';
import { getShadowRootContainer } from '../commonComponents/customUtils';

/**
 * Hook that returns a stable Shadow Root container reference for MUI Poppers.
 * Recomputes only when wcFlow flag, hostRef, or mounted state changes.
 *
 * The `mounted` state ensures hostRef.current is populated before resolving
 * the Shadow Root — avoids returning undefined on the initial render.
 *
 * @param {boolean} isWcFlow - Whether the app is running as a web component
 * @param {React.RefObject} hostRef - Ref attached to the component inside Shadow DOM
 * @returns {ShadowRoot | undefined}
 *
 * @example
 * const popperContainer = useShadowRootContainer(wcFlow, hostRef);
 * const popperContainer = useShadowRootContainer(baseContext?.wcFlow, hostRef);
 */
export const useShadowRootContainer = (isWcFlow, hostRef) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return useMemo(
    () => getShadowRootContainer(isWcFlow, hostRef),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isWcFlow, hostRef?.current, mounted] // mounted ensures re-evaluation after hostRef.current is populated
  );
};
